// ─────────────────────────────────────────────────────────────────────────────
// MobileTabBar.tsx — Fixed bottom navigation bar (mobile only)
//
// Replaces the old hamburger-only menu. Three persistent, always-reachable
// entry points into the full set of customization options that previously
// lived only in desktop dropdowns:
//   - Pages:     site nav + framework switcher
//   - Customize: colour scheme + page layout
//   - More:      FE pattern + backend architecture
// ─────────────────────────────────────────────────────────────────────────────

import { Compass, Palette, Settings2 } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { openMobileSheet } from '@/store/uiSlice';

const TABS = [
  { id: 'pages' as const,     label: 'Pages',     icon: Compass },
  { id: 'customize' as const, label: 'Customize', icon: Palette },
  { id: 'more' as const,      label: 'More',       icon: Settings2 },
];

export function MobileTabBar() {
  const dispatch     = useAppDispatch();
  const mobileSheet  = useAppSelector((s) => s.ui.mobileSheet);

  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-40 md:hidden
                 flex items-stretch justify-around
                 backdrop-blur-md border-t border-[var(--color-border)]"
      style={{
        // Was hardcoded bg-white/95 dark:bg-navy-900/95 — now follows the
        // active colour scheme like everything else (see applyColourScheme).
        backgroundColor: 'color-mix(in srgb, var(--color-surface) 95%, transparent)',
        // env(safe-area-inset-bottom) is 0 on most Android Chrome tabs (it's
        // mainly an iOS/PWA concept) — max() guarantees a minimum clearance
        // either way so the bar doesn't sit flush against Android's own
        // gesture/nav bar. translateZ forces its own compositing layer,
        // which reduces visual jank while Chrome's toolbar shows/hides
        // during scroll (the "shrinks behind the phone's button" report).
        paddingBottom: 'max(env(safe-area-inset-bottom), 10px)',
        transform: 'translateZ(0)',
      }}
      aria-label="Customize and navigate"
    >
      {TABS.map((tab) => {
        const Icon = tab.icon;
        const isActive = mobileSheet === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => dispatch(openMobileSheet(tab.id))}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 min-h-[56px] min-w-[64px] transition-colors
              ${isActive ? 'text-accent' : 'text-slate-500 dark:text-slate-400'}`}
            aria-label={tab.label}
            aria-pressed={isActive}
          >
            <Icon size={20} />
            <span className="text-[11px] font-medium">{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
